import { AgGridReact } from 'ag-grid-react';
import { IHeaderParams, ColDef, GridReadyEvent, FirstDataRenderedEvent, RowDataChangedEvent, IDetailCellRendererParams, GridApi, ValueGetterParams, ICellRendererParams } from 'ag-grid-community';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { IInvoiceSummary } from '@nkr/store/billing/actions-admin-invoice';
import React, { useCallback, useMemo } from 'react';
import GridInvoiceCollectionsDetail from './grid-collections-detail';
import 'ag-grid-enterprise';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-bootstrap.css';
import { Label } from 'react-bootstrap';
import { GridWrapper } from './styles';

interface IProps {
    data: IInvoiceSummary[],
    onGridReady: (gridApi: GridReadyEvent) => void
}

const GridInvoiceCollections = (props: IProps) => {
    let gridApi: GridApi = null;

    const updateRow = (invoiceId: number, paymentSum: number) => {
        gridApi.forEachNode((rowNode, index) => {
            let newData = rowNode.data as IInvoiceSummary;
            if (newData.id == invoiceId) {
                newData.amountPaid = paymentSum;
                newData.balanceDue = newData.amountDue - paymentSum;
                rowNode.updateData(newData);
            }
        });
    }

    const onGridReady = useCallback((params: GridReadyEvent) => {
        gridApi = params.api;
        props.onGridReady(params);
    }, []);

    const onFirstDataRendered = useCallback((params: FirstDataRenderedEvent) => {

    }, []);


    const renderDetail = (props: IDetailCellRendererParams) => {
        return (
            <>
                <GridInvoiceCollectionsDetail data={props.data} updateRow={updateRow} />
            </>
        )
    }

    const cols: ColDef[] = [
        {
            colId: 'rank',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
            headerName: 'R#',
            width: 40,
            cellClass: 'list-cell',
            headerClass: 'list-header',
            valueGetter: (props: ValueGetterParams) => props.node.rowIndex + 1,
            sortable: false
        },
        {
            colId: 'centerAlias',
            field: 'centerAlias',
            headerName: 'Center',
            headerClass: 'list-header',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Center</SortableHeaderComponent>,
            width: 155,
            cellClass: 'list-cell',
            sortable: true,
            resizable: false,
            cellRenderer: 'agGroupCellRenderer'
        },
        {
            colId: 'organType',
            field: 'organType',
            headerName: 'Organ Type',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>Organ Type</SortableHeaderComponent>,
            width: 150,
            sortable: true,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IInvoiceSummary;
                let organType = data.organType == null ? '--' : data.organType == 1 ? 'L' : 'K';
                return <Label bsStyle={data.organType == null ? 'default' : data.organType == 1 ? 'warning' : 'danger'} style={{ fontSize: '100%' }}>{organType}</Label>;
            }
        },
        {
            colId: 'invoiceNumber',
            field: 'invoiceNumber',
            headerName: 'Invoice',
            headerClass: 'list-header',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Invoice</SortableHeaderComponent>,
            width: 155,
            cellClass: 'list-cell',
            sortable: true,
            resizable: false
        },
        {
            colId: 'amountDue',
            field: 'amountDue',
            headerName: 'Amt Invoiced',
            headerClass: 'list-header',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Amt Invoiced</SortableHeaderComponent>,
            valueFormatter: params => `\$${params.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            width: 155,
            cellClass: 'list-cell',
            sortable: true,
            resizable: false,
        },
        {
            colId: 'amountPaid',
            field: 'amountPaid',
            headerName: 'Amt Paid',
            headerClass: 'list-header',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Amt Paid</SortableHeaderComponent>,
            valueFormatter: params => `\$${params.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            width: 155,
            cellClass: 'list-cell',
            sortable: true,
            resizable: false
        },
        {
            colId: 'balanceDue',
            field: 'balanceDue',
            headerName: 'Balance Due',
            headerClass: 'list-header',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Balance Due</SortableHeaderComponent>,
            valueFormatter: params => `\$${params.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            width: 155,
            cellClass: 'list-cell',
            sortable: true,
            resizable: false
        },
        {
            colId: 'daysDelinquent',
            field: 'daysDelinquent',
            headerName: 'Days Delinquent',
            headerClass: 'list-header',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Days Delinquent</SortableHeaderComponent>,
            width: 155,
            cellClass: 'list-cell',
            sortable: true,
            resizable: false
        }
    ];

    const columnDefs = useMemo(() => cols, []);
    const rowData = useMemo(() => props.data, [props.data]);
    const detailCellRenderer = useMemo(() => { return renderDetail; }, []);

    return (
        <GridWrapper className="ag-theme-bootstrap">
            <AgGridReact
                rowStyle={{ overflow: "visible" }}
                masterDetail={true}
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={28}
                rowHeight={30}
                suppressRowClickSelection={false}
                suppressCellSelection={true}
                suppressContextMenu={true}
                noRowsOverlayComponentFramework={() => <div>No invoice information available...</div>}
                suppressHorizontalScroll={true}
                onGridReady={onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onRowDataChanged={(e: RowDataChangedEvent) => e.api.sizeColumnsToFit()}
                detailCellRendererFramework={detailCellRenderer}
            />
        </GridWrapper>
    )
}

export default GridInvoiceCollections;